import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Clock, User, GraduationCap, Plus, Edit, Trash2, Calendar, 
  Lock, Unlock, Save, Star, MessageSquare, Loader2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function LMSEnhanced() {
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dripSchedule, setDripSchedule] = useState({});
  const [reviews, setReviews] = useState([]);
  
  // Dialogs
  const [showInstructorDialog, setShowInstructorDialog] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  
  // Forms
  const [instructorForm, setInstructorForm] = useState({
    name: '', email: '', bio: '', photo_url: '', expertise: [], is_active: true
  });
  
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, instructorsRes] = await Promise.all([
        api.get('/tenant/courses'),
        api.get('/tenant/instructors')
      ]);
      setCourses(coursesRes.data.courses || []);
      setInstructors(instructorsRes.data.instructors || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    try {
      const [dripRes, reviewsRes] = await Promise.all([
        api.get(`/tenant/courses/${courseId}/drip-schedule`),
        api.get(`/tenant/courses/${courseId}/reviews`)
      ]);
      setDripSchedule(dripRes.data.drip_schedule || {});
      setReviews(reviewsRes.data.reviews || []);
    } catch (err) {
      console.error('Failed to fetch course details:', err);
    }
  };

  const saveInstructor = async () => {
    try {
      if (editingInstructor) {
        await api.put(`/tenant/instructors/${editingInstructor}`, instructorForm);
        toast({ title: 'Berhasil', description: 'Instruktur berhasil diupdate' });
      } else {
        await api.post('/tenant/instructors', instructorForm);
        toast({ title: 'Berhasil', description: 'Instruktur berhasil ditambahkan' });
      }
      setShowInstructorDialog(false);
      resetInstructorForm();
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menyimpan instruktur', variant: 'destructive' });
    }
  };

  const resetInstructorForm = () => {
    setInstructorForm({ name: '', email: '', bio: '', photo_url: '', expertise: [], is_active: true });
    setEditingInstructor(null);
  };

  const editInstructor = (instructor) => {
    setEditingInstructor(instructor.id);
    setInstructorForm({ ...instructor });
    setShowInstructorDialog(true);
  };

  const assignInstructor = async (courseId, instructorId) => {
    try {
      await api.put(`/tenant/courses/${courseId}/instructor?instructor_id=${instructorId}`);
      toast({ title: 'Berhasil', description: 'Instruktur berhasil ditambahkan ke kursus' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menambahkan instruktur', variant: 'destructive' });
    }
  };

  const saveDripSchedule = async () => {
    if (!selectedCourse) return;
    
    const schedules = Object.entries(dripSchedule).map(([lessonId, config]) => ({
      lesson_id: lessonId,
      drip_type: config.drip_type || 'days_after_enrollment',
      drip_value: config.drip_value || 0,
      specific_date: config.specific_date || null
    }));
    
    try {
      await api.put(`/tenant/courses/${selectedCourse.id}/drip-schedule`, schedules);
      toast({ title: 'Berhasil', description: 'Jadwal drip content berhasil disimpan' });
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menyimpan jadwal', variant: 'destructive' });
    }
  };

  const updateDrip = (lessonId, field, value) => {
    setDripSchedule({
      ...dripSchedule,
      [lessonId]: {
        ...dripSchedule[lessonId],
        [field]: value
      }
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="lms-enhanced-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">LMS Management</h1>
          <p className="text-muted-foreground">Kelola instruktur, drip content, dan review kursus</p>
        </div>
      </div>

      <Tabs defaultValue="instructors">
        <TabsList>
          <TabsTrigger value="instructors"><User className="h-4 w-4 mr-1" />Instruktur ({instructors.length})</TabsTrigger>
          <TabsTrigger value="drip"><Clock className="h-4 w-4 mr-1" />Drip Content</TabsTrigger>
          <TabsTrigger value="reviews"><Star className="h-4 w-4 mr-1" />Reviews</TabsTrigger>
        </TabsList>

        {/* Instructors Tab */}
        <TabsContent value="instructors" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetInstructorForm(); setShowInstructorDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" /> Tambah Instruktur
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {instructors.map((instructor) => (
              <Card key={instructor.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={instructor.photo_url} />
                      <AvatarFallback className="text-lg">{instructor.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="font-semibold">{instructor.name}</h3>
                      <p className="text-sm text-muted-foreground">{instructor.email}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {instructor.expertise?.slice(0, 3).map((exp, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{exp}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{instructor.bio}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span><GraduationCap className="h-4 w-4 inline mr-1" />{instructor.course_count || 0} kursus</span>
                      <span><Star className="h-4 w-4 inline mr-1" />{instructor.average_rating || 0}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => editInstructor(instructor)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {instructors.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-muted-foreground">
                  Belum ada instruktur. Klik "Tambah Instruktur" untuk membuat.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Drip Content Tab */}
        <TabsContent value="drip" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Course List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pilih Kursus</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {courses.map((course) => (
                    <button
                      key={course.id}
                      onClick={() => {
                        setSelectedCourse(course);
                        fetchCourseDetails(course.id);
                      }}
                      className={`w-full p-3 border rounded-lg text-left transition-all ${
                        selectedCourse?.id === course.id ? 'border-[#2D6CDF] bg-blue-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <p className="font-medium">{course.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {course.chapter_count || 0} chapter • {course.lesson_count || 0} lesson
                      </p>
                      {course.has_drip_content && (
                        <Badge className="mt-1 bg-purple-500">Drip Active</Badge>
                      )}
                    </button>
                  ))}
                  {courses.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">Belum ada kursus</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Drip Schedule */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Jadwal Drip Content</CardTitle>
                  {selectedCourse && (
                    <Button size="sm" onClick={saveDripSchedule}>
                      <Save className="h-4 w-4 mr-1" /> Simpan
                    </Button>
                  )}
                </div>
                <CardDescription>Atur kapan setiap lesson bisa diakses oleh siswa</CardDescription>
              </CardHeader>
              <CardContent>
                {selectedCourse ? (
                  <div className="space-y-3">
                    {selectedCourse.lessons?.length > 0 ? (
                      selectedCourse.lessons.map((lesson, i) => (
                        <div key={lesson.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {dripSchedule[lesson.id] ? (
                                <Lock className="h-4 w-4 text-amber-500" />
                              ) : (
                                <Unlock className="h-4 w-4 text-emerald-500" />
                              )}
                              <span className="font-medium">Lesson {i + 1}: {lesson.title}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Tipe Drip</Label>
                              <Select 
                                value={dripSchedule[lesson.id]?.drip_type || 'none'}
                                onValueChange={(v) => updateDrip(lesson.id, 'drip_type', v === 'none' ? null : v)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Tidak ada drip</SelectItem>
                                  <SelectItem value="days_after_enrollment">Hari setelah enrollment</SelectItem>
                                  <SelectItem value="specific_date">Tanggal spesifik</SelectItem>
                                  <SelectItem value="after_completion">Setelah lesson sebelumnya</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              {dripSchedule[lesson.id]?.drip_type === 'days_after_enrollment' && (
                                <>
                                  <Label className="text-xs">Hari ke-</Label>
                                  <Input 
                                    type="number"
                                    className="h-8"
                                    value={dripSchedule[lesson.id]?.drip_value || 0}
                                    onChange={(e) => updateDrip(lesson.id, 'drip_value', parseInt(e.target.value) || 0)}
                                  />
                                </>
                              )}
                              {dripSchedule[lesson.id]?.drip_type === 'specific_date' && (
                                <>
                                  <Label className="text-xs">Tanggal</Label>
                                  <Input 
                                    type="date"
                                    className="h-8"
                                    value={dripSchedule[lesson.id]?.specific_date || ''}
                                    onChange={(e) => updateDrip(lesson.id, 'specific_date', e.target.value)}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-4">
                        Kursus ini belum memiliki lesson
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Pilih kursus untuk mengatur jadwal drip content
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Course List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pilih Kursus</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {courses.map((course) => (
                    <button
                      key={course.id}
                      onClick={() => {
                        setSelectedCourse(course);
                        fetchCourseDetails(course.id);
                      }}
                      className={`w-full p-3 border rounded-lg text-left transition-all ${
                        selectedCourse?.id === course.id ? 'border-[#2D6CDF] bg-blue-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <p className="font-medium">{course.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        <span className="text-sm">{course.average_rating || 0} ({course.review_count || 0} review)</span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Reviews */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Review Kursus</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedCourse ? (
                  <div className="space-y-4">
                    {reviews.length > 0 ? (
                      reviews.map((review) => (
                        <div key={review.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{review.customer_name}</p>
                              <div className="flex items-center gap-1 mt-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star 
                                    key={star}
                                    className={`h-4 w-4 ${star <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`}
                                  />
                                ))}
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(review.created_at).toLocaleDateString('id-ID')}
                            </span>
                          </div>
                          {review.title && <p className="font-medium mt-2">{review.title}</p>}
                          <p className="text-sm text-muted-foreground mt-1">{review.content}</p>
                          {review.tenant_reply && (
                            <div className="mt-3 p-3 bg-muted rounded-lg">
                              <p className="text-xs font-medium mb-1">Balasan Anda:</p>
                              <p className="text-sm">{review.tenant_reply}</p>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        Belum ada review untuk kursus ini
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Pilih kursus untuk melihat review
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Instructor Dialog */}
      <Dialog open={showInstructorDialog} onOpenChange={setShowInstructorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingInstructor ? 'Edit Instruktur' : 'Tambah Instruktur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama</Label>
              <Input 
                value={instructorForm.name}
                onChange={(e) => setInstructorForm({ ...instructorForm, name: e.target.value })}
                placeholder="Nama instruktur"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                value={instructorForm.email}
                onChange={(e) => setInstructorForm({ ...instructorForm, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label>Photo URL</Label>
              <Input 
                value={instructorForm.photo_url}
                onChange={(e) => setInstructorForm({ ...instructorForm, photo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea 
                value={instructorForm.bio}
                onChange={(e) => setInstructorForm({ ...instructorForm, bio: e.target.value })}
                placeholder="Deskripsi singkat tentang instruktur..."
                rows={3}
              />
            </div>
            <div>
              <Label>Keahlian (pisahkan dengan koma)</Label>
              <Input 
                value={instructorForm.expertise?.join(', ') || ''}
                onChange={(e) => setInstructorForm({ ...instructorForm, expertise: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="Web Development, UI/UX, Marketing"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructorDialog(false)}>Batal</Button>
            <Button onClick={saveInstructor}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
